import React, { createContext, useContext, useState, useCallback } from 'react';

const MessageContext = createContext();

export function MessageProvider({ children }) {
  const [messages, setMessages] = useState([]);

  // 生成唯一ID
  const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // 显示消息
  const showMessage = useCallback((text, type = 'info', options = {}) => {
    const {
      duration = 3000,
      closable = true,
      ...otherOptions
    } = options;

    const message = {
      id: generateId(),
      text,
      type,
      duration,
      closable,
      timestamp: Date.now(),
      ...otherOptions
    };

    // console.log('📢 Show message:', message);

    setMessages(prev => [...prev, message]);

    // 自动隐藏消息
    if (duration > 0) {
      setTimeout(() => {
        hideMessage(message.id);
      }, duration);
    }

    return message.id;
  }, []);

  // 隐藏消息（带动画）
  const hideMessage = useCallback((id) => {
    // console.log('🗑️ Hide message:', id);
    // 先标记为正在离开状态
    setMessages(prev => prev.map(msg => 
      msg.id === id ? { ...msg, isLeaving: true } : msg
    ));
    
    // 300ms后真正移除消息（等待退出动画完成）
    setTimeout(() => {
      setMessages(prev => prev.filter(msg => msg.id !== id));
    }, 300);
  }, []);

  // 清空所有消息（带动画）
  const clearMessages = useCallback(() => {
    console.log('🧹 Clear all messages');
    // 先标记所有消息为正在离开状态
    setMessages(prev => prev.map(msg => ({ ...msg, isLeaving: true })));
    
    // 300ms后真正清空所有消息
    setTimeout(() => {
      setMessages([]);
    }, 300);
  }, []);

  // 便捷方法
  const success = useCallback((text, options) => showMessage(text, 'success', options), [showMessage]);
  const error = useCallback((text, options) => showMessage(text, 'error', options), [showMessage]);
  const warning = useCallback((text, options) => showMessage(text, 'warning', options), [showMessage]);
  const info = useCallback((text, options) => showMessage(text, 'info', options), [showMessage]);

  const value = {
    messages,
    showMessage,
    hideMessage,
    clearMessages,
    success,
    error,
    warning,
    info
  };

  return (
    <MessageContext.Provider value={value}>
      {children}
    </MessageContext.Provider>
  );
}

export function useMessage() {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error('useMessage must be used within a MessageProvider');
  }
  return context;
}
